import { createContext, useState, useCallback, useContext, useReducer, useMemo } from 'react';
import { get, omit } from 'lodash';

import {
  addExplorationCell,
  addExplorationCellsAt,
  deleteExplorationCell,
  insertConversationStart,
  setExplorationCells,
} from '@/core/exploration';
import {
  belongsToConversation,
  buildConversationCell,
  getCell,
  isChatCell,
  isConversationCell,
  isConversationPart,
  isInvalidCell,
  isUserMessage,
  removeConversationId,
} from '@/core/cell';

import { notNil } from '@/lib/utils';

import {
  Cell,
  Exploration,
  ExplorationParameter,
  ExplorationParameters,
  Fields,
  Metric,
  Model,
  QueryVariables,
  CanvasNode,
  CanvasEdge,
  Pipeline,
  ChatMode,
  Conversation,
  SortItem,
} from '../types';
import { getVariableDefinitions, getQueryVariablesFromParameters } from '../utils';
import {
  importExplorationCellsToExploration,
  createExplorationCellInstance,
  duplicateExplorationCell,
  getFieldsByCellId,
} from './utils';
import {
  ensureValidLayout,
  getRowEndIndex,
  getRowStartIndex,
  isCellFirstInRow,
  isCellInMultiRow,
  isCellLastInRow,
  moveCell,
  swapCells,
} from './exploration-layout/utils';
import { createDrilldownCell } from '../utils/drilldown';
import { usePersistCell, CopiedCell } from '../utils/use-persist-cell';
import { useExplorationUIContext } from './exploration-ui-context';
import { useConversationRepositoryContext } from '../conversation/conversation-repository-context';
import { useConversationExplorationContext } from '../conversation/conversation-exploration-context';

export interface QueryMeta {
  recordType?: Fields;
  grouping?: { key: string }[];
}

type ExplorationState = {
  queryMeta: { [cellId: string]: QueryMeta | null };
};

type SetQueryMetaAction = {
  type: 'SET_QUERY_META';
  payload: { cellId: string; meta: QueryMeta | null };
};

type Action = SetQueryMetaAction;

const getInitialState = (): ExplorationState => ({
  queryMeta: {},
});

const reducer = (state: ExplorationState, action: Action): ExplorationState => {
  switch (action.type) {
    case 'SET_QUERY_META':
      return {
        ...state,
        queryMeta: {
          ...state.queryMeta,
          [action.payload.cellId]: action.payload.meta,
        },
      };
    default:
      return state;
  }
};

export interface ExplorationContextValue {
  exploration: Exploration;
  cellCount: number;
  copiedCell?: CopiedCell;
  persistCopiedCell: (cell: { cell: Cell; dependencies: Cell[] }) => void;
  removeCopiedCell: () => void;
  setExploration: (exploration: Exploration, parameters?: ExplorationParameters) => void;
  upsertExploration: (
    exploration: Exploration,
    parameters: ExplorationParameters | null,
  ) => Promise<{ explorationId: string }>;
  deleteExploration: (explorationId: string) => Promise<void>;
  setCellById: (cell: Cell, cellId: string) => void;
  getCellById: (cellId: string) => Cell | undefined;
  duplicateCell: (cellId: string) => { exploration: Exploration; cell?: Cell };
  addCells: (
    cell: Cell,
    dependencies: Cell[],
    index: number,
  ) => { exploration: Exploration; cell: Cell; dependencies: Cell[] };
  createCellInstance: (cellId: string) => { exploration: Exploration; cell?: Cell };
  deleteCell: (cellId: string) => void;
  moveCell: (
    fromIndex: number,
    toIndex: number,
    after?: boolean,
    mergeIntoRow?: boolean,
    height?: number,
  ) => void;
  moveCellUp: (index: number) => void;
  moveCellDown: (index: number) => void;
  setQueryMeta: (payload: SetQueryMetaAction['payload']) => void;
  getQueryMeta: (cellId: string) => QueryMeta | null;
  getFieldsByCellId: (cellId: string) => Fields;
  parameters: ExplorationParameters;
  setParameter: (key: keyof ExplorationParameters, value: ExplorationParameter) => void;
  getParameter: (key: keyof ExplorationParameters) => ExplorationParameter | undefined;
  getParameters: () => ExplorationParameters;
  getVariables: () => QueryVariables;
  clearParameter: (key: keyof ExplorationParameters) => void;
  drilldownFromPipeline: (input: {
    pipeline: Pipeline;
    title?: string;
    afterCellId: string;
    sort?: SortItem;
  }) => void;
  canvasState: { nodes?: CanvasNode[]; edges?: CanvasEdge[] };
  setCanvasState: (state: { nodes?: CanvasNode[]; edges?: CanvasEdge[] }) => void;
  isConversationSubject: (cell: Cell) => boolean;
  createConversation: (
    searchQuery: string,
    sources: string[],
    mode: ChatMode,
    index: number,
    correlationId?: string,
  ) => Promise<{
    conversation: Conversation | null;
    error: Error | undefined;
    exploration: Exploration;
  }>;
  removeConversation(conversationId: string): void;
  acceptConversation(
    conversationId: string,
    acceptedCells: Cell[],
    baseExploration?: Exploration,
  ): void;
}

const defaultContextValue = Symbol();

const ExplorationContext = createContext<ExplorationContextValue | typeof defaultContextValue>(
  defaultContextValue,
);

export const useExplorationContext = () => {
  const context = useContext(ExplorationContext);
  if (context === defaultContextValue) {
    throw new Error('useExplorationContext must be used within a ExplorationContextProvider');
  }
  return context;
};

export interface ExplorationContextProviderProps {
  exploration: Exploration;
  models: Model[];
  metrics: Metric[];
  parameters: ExplorationParameters;
  setExploration: (
    exploration: Exploration,
    parameters: ExplorationParameters | null,
  ) => Exploration;
  setParameters: (parameters: ExplorationParameters) => void;
  upsertExploration: (
    exploration: Exploration,
    parameters: ExplorationParameters | null,
  ) => Promise<{ explorationId: string }>;
  deleteExploration: (explorationId: string) => Promise<void>;
  children: React.ReactNode;
}

export const ExplorationContextProvider = (props: ExplorationContextProviderProps) => {
  const {
    exploration,
    models,
    metrics,
    parameters,
    setExploration,
    setParameters,
    upsertExploration,
    deleteExploration,
    children,
  } = props;
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [canvasState, setCanvasState] = useState<{
    nodes?: CanvasNode[];
    edges?: CanvasEdge[];
  }>({});

  const {
    selectedCell,
    selectCell,
    deselectCell,
    scrollToCell,
    newConversationSubjectId,
    setNewConversationSubjectId,
    setConversationDraft,
    setNewCellIndex,
  } = useExplorationUIContext();
  const { copiedCell, persistCopiedCell, removeCopiedCell } = usePersistCell();
  const { startConversation, getConversation } = useConversationRepositoryContext();
  const { isWithinConversation } = useConversationExplorationContext();

  const cells = exploration.view.cells;

  const getCellById = useCallback((cellId: string) => getCell(cells, cellId), [cells]);

  const setCellById = useCallback(
    (updatedCell: Cell, cellId: string) => {
      const updatedExploration = {
        ...exploration,
        view: {
          ...exploration.view,
          cells: cells.map((cell) => (cell.id === cellId ? updatedCell : cell)),
        },
      };

      setExploration(updatedExploration, parameters);
    },
    [exploration, parameters, cells, setExploration],
  );

  const duplicateCell = useCallback(
    (cellId: string) => {
      const result = duplicateExplorationCell(exploration, cellId);

      setExploration(result.exploration, parameters);

      return result;
    },
    [exploration, parameters, setExploration],
  );

  const addCells = useCallback(
    (cell: Cell, dependencies: Cell[], index: number) => {
      const result = importExplorationCellsToExploration(exploration, cell, dependencies, index);
      const updatedExploration = setExploration(result.exploration, parameters);
      return { ...result, exploration: updatedExploration };
    },
    [exploration, parameters, setExploration],
  );

  const createCellInstance = useCallback(
    (cellId: string) => {
      const result = createExplorationCellInstance(exploration, cellId);

      setExploration(result.exploration, parameters);

      return result;
    },
    [exploration, parameters, setExploration],
  );

  const deleteCell = useCallback(
    (cellId: string) => {
      setExploration(deleteExplorationCell(exploration, cellId), parameters);
      if (selectedCell?.id === cellId) {
        deselectCell();
      }
    },
    [exploration, parameters, selectedCell?.id, setExploration, deselectCell],
  );

  const moveCellUp = useCallback(
    (index: number) => {
      let newCells = cells;

      if (!isCellInMultiRow(cells, index)) {
        // Jump before first cell in previous row
        newCells = moveCell(cells, index, getRowStartIndex(cells, index - 1), false, false);
      } else if (isCellFirstInRow(cells, index)) {
        // Detach from row = jump before itself (with mergeIntoRow = false)
        newCells = moveCell(cells, index, index, false, false);
      } else {
        // Cell is non-first in multi-row: move within row
        newCells = swapCells(cells, index, index - 1);
      }
      setExploration(
        {
          ...exploration,
          view: {
            ...exploration.view,
            cells: ensureValidLayout(newCells),
          },
        },
        parameters,
      );

      if (selectedCell !== null) {
        selectCell(selectedCell.id);
      }
    },
    [cells, exploration, parameters, selectedCell, selectCell, setExploration],
  );

  const moveCellDown = useCallback(
    (index: number) => {
      let newCells = cells;

      if (!isCellInMultiRow(cells, index)) {
        // Jump after last cell in next row
        newCells = moveCell(cells, index, getRowEndIndex(cells, index + 1), true, false);
      } else if (isCellLastInRow(cells, index)) {
        // Detach from row = jump after itself (with mergeIntoRow = false)
        newCells = moveCell(cells, index, index, true, false);
      } else {
        // Cell is non-last in multi-row: move within row
        newCells = swapCells(cells, index, index + 1);
      }
      setExploration(
        {
          ...exploration,
          view: {
            ...exploration.view,
            cells: ensureValidLayout(newCells),
          },
        },
        parameters,
      );

      if (selectedCell !== null) {
        selectCell(selectedCell.id);
      }
    },
    [exploration, cells, parameters, selectedCell, selectCell, setExploration],
  );

  const wrappedMoveCell = useCallback(
    (
      fromIndex: number,
      toIndex: number,
      after = false,
      mergeIntoRow = false,
      cellHeight?: number,
    ) => {
      const newCells = moveCell(cells, fromIndex, toIndex, after, mergeIntoRow, cellHeight);
      setExploration(
        { ...exploration, view: { ...exploration.view, cells: newCells } },
        parameters,
      );

      if (selectedCell !== null) {
        selectCell(selectedCell.id);
      }
    },
    [cells, exploration, parameters, selectedCell, selectCell, setExploration],
  );

  const getQueryMeta = useCallback(
    (cellId: string) => get(state.queryMeta, cellId, null),
    [state.queryMeta],
  );

  const setQueryMeta = useCallback(
    (payload: SetQueryMetaAction['payload']) => dispatch({ type: 'SET_QUERY_META', payload }),
    [],
  );

  const setParameter = useCallback(
    (key: keyof ExplorationParameters, value: ExplorationParameter) =>
      setParameters({ ...parameters, [key]: value }),
    [parameters, setParameters],
  );

  const getParameter = useCallback(
    (key: keyof ExplorationParameters) => parameters[key],
    [parameters],
  );

  const clearParameter = useCallback(
    (key: keyof ExplorationParameters) => setParameters(omit(parameters, key)),
    [parameters, setParameters],
  );

  // It is paradoxical but the variables are sorted here because their order is not important. It is done so because
  // reordering variables do not cause dependent queries to refetch.
  const getVariables = useCallback(
    () =>
      getQueryVariablesFromParameters(getVariableDefinitions(exploration), parameters).sort(
        (a, b) => a.key.localeCompare(b.key),
      ),
    [exploration, parameters],
  );

  const isConversationSubject = useCallback(
    (cell: Cell) => {
      return (
        cell.id === newConversationSubjectId ||
        (!isConversationCell(cell) &&
          !isChatCell(cell) &&
          isConversationPart(cell) &&
          !isWithinConversation)
      );
    },
    [newConversationSubjectId, isWithinConversation],
  );

  const createConversation = useCallback(
    async (
      searchQuery: string,
      sources: string[],
      mode: ChatMode,
      index: number,
      correlationId?: string,
    ) => {
      const conversationCell = buildConversationCell();
      const subjectId = newConversationSubjectId;
      const conversationSubject = subjectId !== null ? getCellById(subjectId) : null;

      let updatedExploration: Exploration;
      if (notNil(conversationSubject) && !isInvalidCell(conversationSubject)) {
        updatedExploration = setExploration(
          insertConversationStart(exploration, conversationCell, conversationSubject.id),
          parameters,
        );
      } else {
        const result = addCells(conversationCell, [], index);
        updatedExploration = result.exploration;
      }

      setNewConversationSubjectId(null);
      setNewCellIndex(null);

      const result = await startConversation(
        conversationCell.conversationId,
        searchQuery,
        sources,
        mode,
        conversationSubject?.id ?? null,
        correlationId,
      );

      if (result.error) {
        return { ...result, exploration };
      }

      return {
        ...result,
        exploration: updatedExploration,
      };
    },
    [
      addCells,
      exploration,
      getCellById,
      newConversationSubjectId,
      parameters,
      setExploration,
      setNewCellIndex,
      setNewConversationSubjectId,
      startConversation,
    ],
  );

  const drilldownFromPipeline = useCallback(
    (input: { pipeline: Pipeline; title?: string; afterCellId: string; sort?: SortItem }) => {
      const { pipeline, title, afterCellId, sort } = input;

      const drilldownCell = createDrilldownCell(pipeline, title, sort);
      const updatedExploration = addExplorationCell(exploration, drilldownCell, afterCellId);

      setExploration(updatedExploration, parameters);
      scrollToCell(drilldownCell.id);
      selectCell(drilldownCell.id);
    },
    [exploration, parameters, scrollToCell, selectCell, setExploration],
  );

  const acceptConversation = useCallback(
    (conversationId: string, acceptedCells: Cell[], baseExploration?: Exploration) => {
      const conversationCellIndex = exploration.view.cells.findIndex(
        (cell) => isConversationCell(cell) && cell.conversationId === conversationId,
      );
      const updatedCells = cells
        // Filter out conversation cell with given conversationId
        .filter((cell) => !isConversationCell(cell) || cell.conversationId !== conversationId)
        // Remove conversationId from other cells
        .map((cell) =>
          belongsToConversation(cell, conversationId) ? removeConversationId(cell) : cell,
        );
      setExploration(
        addExplorationCellsAt(
          setExplorationCells(baseExploration ?? exploration, updatedCells),
          acceptedCells,
          conversationCellIndex,
        ),
        parameters,
      );
    },
    [cells, exploration, parameters, setExploration],
  );

  const removeConversation = useCallback(
    (conversationId: string) => {
      const conversation = getConversation(conversationId);
      const conversationCellIndex = exploration.view.cells.findIndex(
        (cell) => isConversationCell(cell) && cell.conversationId === conversationId,
      );
      const conversationSubject = exploration.view.cells.find(
        (cell) => isConversationSubject(cell) && belongsToConversation(cell, conversationId),
      );

      if (conversationSubject === undefined) {
        setNewCellIndex(conversationCellIndex);
        const firstMessage = conversation?.messages.find(isUserMessage);
        setConversationDraft('new', firstMessage?.message ?? null);
      }

      acceptConversation(conversationId, []);
    },
    [
      getConversation,
      exploration.view.cells,
      acceptConversation,
      isConversationSubject,
      setNewCellIndex,
      setConversationDraft,
    ],
  );

  const cellCount = cells.length;

  const stableSetExploration = useCallback(
    (exploration: Exploration, newParameters?: ExplorationParameters) =>
      setExploration(exploration, newParameters === undefined ? parameters : newParameters),
    [setExploration, parameters],
  );

  const stableGetFieldsByCellId = useCallback(
    (cellId: string) => getFieldsByCellId(cellId, exploration, models, getVariables(), metrics),
    [exploration, models, metrics, getVariables],
  );

  const stableGetParameters = useCallback(() => parameters, [parameters]);

  const contextValue = useMemo(
    () => ({
      exploration,
      cellCount,
      copiedCell,
      persistCopiedCell,
      removeCopiedCell,
      setExploration: stableSetExploration,
      upsertExploration,
      deleteExploration,
      setCellById,
      getCellById,
      duplicateCell,
      addCells,
      createCellInstance,
      deleteCell,
      moveCell: wrappedMoveCell,
      moveCellUp,
      moveCellDown,
      setQueryMeta,
      getQueryMeta,
      getFieldsByCellId: stableGetFieldsByCellId,
      parameters,
      setParameter,
      getParameter,
      getParameters: stableGetParameters,
      getVariables,
      clearParameter,
      drilldownFromPipeline,
      isConversationSubject,
      canvasState,
      setCanvasState,
      createConversation,
      removeConversation,
      acceptConversation,
    }),
    [
      exploration,
      cellCount,
      copiedCell,
      persistCopiedCell,
      removeCopiedCell,
      stableSetExploration,
      upsertExploration,
      deleteExploration,
      setCellById,
      getCellById,
      duplicateCell,
      addCells,
      createCellInstance,
      deleteCell,
      wrappedMoveCell,
      moveCellUp,
      moveCellDown,
      setQueryMeta,
      getQueryMeta,
      stableGetFieldsByCellId,
      parameters,
      setParameter,
      getParameter,
      stableGetParameters,
      getVariables,
      clearParameter,
      drilldownFromPipeline,
      isConversationSubject,
      canvasState,
      setCanvasState,
      createConversation,
      removeConversation,
      acceptConversation,
    ],
  );

  return <ExplorationContext.Provider value={contextValue}>{children}</ExplorationContext.Provider>;
};
